// frontend/src/pages/AuditLog/AuditLogs.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText, ArrowLeft, Loader2, AlertCircle, User,
  Building, Package, Globe, Clock
} from "lucide-react";
import "./AuditLogs.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AuditLogs = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLog = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${url}/api/audit-logs/${id}`, {
          headers: getAuthHeaders(),
        });
        if (res.data.success) {
          setLog(res.data.data);
        } else {
          toast.error(res.data.message || "Failed to load audit log");
          navigate("/ViewAuditLogs");
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load audit log");
        navigate("/ViewAuditLogs");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchLog();
  }, [id, navigate]);

  const getUserName = (u) => {
    if (!u) return "System";
    return (
      u.name ||
      u.Usercomp_sure_name ||
      u.Usercomp_email ||
      u.email ||
      "Unknown User"
    );
  };

  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="auditlog-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading audit log...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="form-wrapper">
        <div className="auditlog-form-container">
          <div className="error-container">
            <AlertCircle size={32} color="#dc3545" />
            <p>Audit log not found</p>
            <button
              className="cancel-btn"
              onClick={() => navigate("/ViewAuditLogs")}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="auditlog-form-container">
        <button
          className="close-btn"
          onClick={() => navigate("/ViewAuditLogs")}
          title="Go Back"
        >
          <ArrowLeft size={20} />
        </button>

        <div className="form-header">
          <FileText size={24} />
          <h2 className="form-title">Audit Log Details</h2>
          <p className="form-subtitle">
            Recorded activity for {log.module} — {log.action}
          </p>
        </div>

        <div className="entity-info">
          <div className="info-item">
            <User size={14} />
            <span>
              <strong>User:</strong> {getUserName(log.userId)}
            </span>
          </div>
          <div className="info-item">
            <Building size={14} />
            <span>
              <strong>Company:</strong>{" "}
              {log.companyId?.company_name || "—"}
            </span>
          </div>
          {log.warehouseId?.warehouse_name && (
            <div className="info-item">
              <Package size={14} />
              <span>
                <strong>Warehouse:</strong> {log.warehouseId.warehouse_name}
              </span>
            </div>
          )}
          <div className="info-item">
            <Globe size={14} />
            <span>
              <strong>IP:</strong> {log.ipAddress || "—"}
            </span>
          </div>
          <div className="info-item">
            <Clock size={14} />
            <span>
              <strong>Date:</strong> {formatDateTime(log.createdAt)}
            </span>
          </div>
        </div>

        <div className="auditlog-block">
          <h4>Summary</h4>
          <div className="form-field filled">
            <input
              type="text"
              value={log.action}
              readOnly
              style={{ backgroundColor: "#f8f9fa" }}
            />
            <label>Action</label>
          </div>
          <div className="form-field filled">
            <input
              type="text"
              value={log.module}
              readOnly
              style={{ backgroundColor: "#f8f9fa" }}
            />
            <label>Module</label>
          </div>
          <div className="form-field filled">
            <textarea
              value={log.description || "—"}
              readOnly
              rows={3}
              style={{ backgroundColor: "#f8f9fa" }}
            />
            <label>Description</label>
          </div>
        </div>

        {(log.oldData || log.newData) && (
          <div className="auditlog-block">
            <h4>Changes</h4>
            {log.oldData && (
              <div className="form-field filled">
                <textarea
                  value={JSON.stringify(log.oldData, null, 2)}
                  readOnly
                  rows={6}
                  style={{ backgroundColor: "#f8f9fa", fontFamily: "monospace" }}
                />
                <label>Old Data</label>
              </div>
            )}
            {log.newData && (
              <div className="form-field filled">
                <textarea
                  value={JSON.stringify(log.newData, null, 2)}
                  readOnly
                  rows={6}
                  style={{ backgroundColor: "#f8f9fa", fontFamily: "monospace" }}
                />
                <label>New Data</label>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => navigate("/ViewAuditLogs")}
          >
            Back to List
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;